import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import { caCities } from "@/data/site";
import { cityRegions } from "@/data/cityRegions";
import { blogPosts } from "@/data/blogPosts";

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title: "Sitemap | McKay Roofing & Solar" },
      { name: "description", content: "Browse every page on the McKay Roofing & Solar website: roofing services, solar services, service areas and company pages." },
      { name: "keywords", content: "sitemap, site index, McKay Roofing & Solar pages" },
      { property: "og:title", content: "Sitemap – McKay Roofing & Solar" },
      { property: "og:description", content: "Every page on mckayroofingsolar.com, grouped so you can find what you need fast." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/sitemap" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/sitemap" }],
  }),
  component: SitemapPage,
});

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      <ul className="mt-4 space-y-2 text-sm">{children}</ul>
    </div>
  );
}

function Item({ to, label }: { to: string; label: string }) {
  return (
    <li>
      <Link to={to} className="text-muted-foreground transition-colors hover:text-primary">
        {label}
      </Link>
    </li>
  );
}

function SitemapPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h1 className="text-5xl font-extrabold text-balance sm:text-6xl">Sitemap</h1>
            <p className="mt-4 max-w-2xl text-primary-foreground/85">
              Every page on our site, grouped so you can find what you need fast. Prefer the
              machine-readable version?{" "}
              <a href="/sitemap.xml" className="underline underline-offset-4">View our sitemap.xml</a>.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            <Group title="Company">
              <Item to="/" label="Home" />
              <Item to="/about" label="About" />
              <Item to="/why-choose-us" label="Why Choose Us" />
              <Item to="/gallery" label="Gallery" />
              <Item to="/recent-work" label="Recent Work" />
              <Item to="/roofing-materials" label="Types of Roofing Materials" />
              <Item to="/utility-companies" label="Solar by Utility Company" />
              <Item to="/blog" label="Blog" />
              <Item to="/blog/archive" label="Blog archive (all posts)" />
              <Item to="/expert-tips" label="Expert Tips" />
              <Item to="/faq" label="FAQ" />
              <Item to="/contact" label="Contact" />
            </Group>

            <Group title="Blog Posts">
              {blogPosts.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="text-muted-foreground transition-colors hover:text-primary"
                  >
                    {p.title.replace(" | McKay", "")}
                  </Link>
                </li>
              ))}
            </Group>

            <Group title="Roofing Services">
              {roofingTiles.map((t) => (
                <Item key={t.slug} to={t.href} label={t.title} />
              ))}
            </Group>

            <Group title="Solar Services">
              {solarTiles.map((t) => (
                <Item key={t.slug} to={t.href} label={t.title} />
              ))}
              <Item to="/book/solar-repair" label="Book Solar Repair" />
            </Group>

            <Group title="Service Areas">
              <Item to="/areas-served" label="Areas Served" />
              <Item to="/areas/california" label="California Hub" />
              {cityRegions.map((r) => (
                <Item key={r.id} to={`/areas/hubs/${r.id}`} label={`${r.label} Hub`} />
              ))}
              {caCities.map((c) => (
                <Item key={c.slug} to={`/areas/california/${c.slug}`} label={c.name} />
              ))}
            </Group>

            <Group title="Legal & Policies">
              <Item to="/privacy" label="Privacy Policy" />
              <Item to="/terms" label="Terms & Conditions" />
              <Item to="/sms-compliance" label="SMS Compliance" />
              <Item to="/ada-compliance" label="ADA & Accessibility" />
            </Group>
          </div>
        </section>
        <CTABlock title="Ready for a free roofing or solar quote?" subtitle="Tell us about your project. We usually respond same day." />
      </main>
      <SiteFooter />
    </div>
  );
}